import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useLanguage } from '../../i18n/LanguageContext';
import { useLocalizedStatic } from '../../i18n/useLocalizedStatic';
import { COMPANY_INFO, ContactInfo } from '../../Utils/Data';
import { mapServiceListItem } from '../../Utils/serviceHelpers';
import { SITE_LOGO_WHITE } from '../../Utils/siteConfig';
import { getServices } from '../../Utils/api';

const FOOTER_NAV = [
  { label: 'Home', to: '/' },
  { label: 'About', to: '/about' },
  { label: 'Gallery', to: '/gallery' },
  { label: 'Services', to: '/services' },
  { label: 'Projects', to: '/projects' },
  { label: 'Blog', to: '/blog' },
  { label: 'Contact', to: '/contact' },
];

const SOCIAL_LINKS = [
  { href: ContactInfo.facebook, icon: 'fa-facebook', label: 'Facebook' },
  { href: ContactInfo.X, icon: 'fa-twitter', label: 'X' },
  { href: ContactInfo.tiktok, icon: 'fa-video-camera', label: 'TikTok' },
  { href: ContactInfo.instagram, icon: 'fa-instagram', label: 'Instagram' },
];

const FOOTER_COPY = {
  intro:
    'Quality Technologies Contracting — part of Al Dawood Holding Group, delivering construction and industrial solutions across Saudi Arabia.',
  openingHours: 'Opening Hours',
  contactInfo: 'Contact Info',
  ourServices: 'Our Services',
  newsletter: 'Newsletter',
  newsletterDesc:
    'Subscribe for project updates, company news, and industry insights from Q-Tech.',
  emailPlaceholder: 'Your Email Address',
  subscribe: 'Subscribe Now',
};

function Footer() {
  const { locale, revision } = useLanguage();
  const [services, setServices] = useState([]);
  const [email, setEmail] = useState('');
  const companyInfo = useLocalizedStatic(COMPANY_INFO);
  const footerNav = useLocalizedStatic(FOOTER_NAV);
  const copy = useLocalizedStatic(FOOTER_COPY);

  useEffect(() => {
    let active = true;

    getServices()
      .then((data) => {
        if (!active) return;
        setServices((data || []).map(mapServiceListItem));
      })
      .catch(() => {
        if (!active) return;
        setServices([]);
      });

    return () => {
      active = false;
    };
  }, [locale, revision]);

  const handleSubscribe = (event) => {
    event.preventDefault();
    const value = email.trim();

    if (!value) {
      toast.error('Please enter your email address.');
      return;
    }

    toast.success('Thanks for subscribing! We will keep you updated.');
    setEmail('');
  };

  return (
    <footer id="rs-footer" className="rs-footer footer-main-home">
      <div className="footer-top">
        <div className="container">
          <div className="row">
            <div className="col-lg-3 pr-20 md-pr-15 md-mb-20">
              <div className="footer-logo mb-51 md-mb-30">
                <Link to="/">
                  <img className="site-logo footer-brand-logo" src={SITE_LOGO_WHITE} alt="Q-Tech Contracting" />
                </Link>
              </div>
              <div className="textwidget">
                <p className="pb-20">{copy.intro}</p>
                <p className="pb-25">
                  <strong>{copy.openingHours}</strong> : {ContactInfo.office_hours}
                </p>
              </div>
              <ul className="footer-social md-mb-30">
                {SOCIAL_LINKS.map((item) => (
                  <li key={item.label}>
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={item.label}
                    >
                      <i className={`fa ${item.icon}`} />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div className="col-lg-3 md-mb-10">
              <h3 className="footer-title">{copy.contactInfo}</h3>
              <ul className="address-widget">
                <li>
                  <i className="fi fi-rr-map-marker-home" />
                  <div className="desc">
                    <a
                      href={ContactInfo.office_maps_share_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {ContactInfo.office_address_line1}
                      <br />
                      {ContactInfo.office_address_line2}
                    </a>
                  </div>
                </li>
                <li>
                  <i className="fi fi-rr-phone-call" />
                  <div className="desc">
                    <a href={`tel:${ContactInfo.contact_phone}`}>{ContactInfo.contact_phone}</a>
                    <br />
                    <a href={`tel:${ContactInfo.contact_phone2}`}>{ContactInfo.contact_phone2}</a>
                  </div>
                </li>
                <li>
                  <i className="fi fi-rr-envelope-plus" />
                  <div className="desc">
                    <a href={`mailto:${ContactInfo.contact_email}`}>
                      {ContactInfo.contact_email}
                    </a>
                    <br />
                    <a href={`mailto:${ContactInfo.hr_email}`}>{ContactInfo.hr_email}</a>
                  </div>
                </li>
              </ul>
            </div>
            <div className="col-lg-3 md-mb-10">
              <h3 className="footer-title">{copy.ourServices}</h3>
              <ul className="site-map">
                {services.map((service) => (
                  <li key={service.id}>
                    <Link to={service.link}>{service.title}</Link>
                  </li>
                ))}
              </ul>
            </div>
            <div className="col-lg-3">
              <h3 className="footer-title">{copy.newsletter}</h3>
              <p className="widget-desc">{copy.newsletterDesc}</p>
              <form className="footer-newsletter" onSubmit={handleSubscribe}>
                <p>
                  <input
                    type="email"
                    name="email"
                    placeholder={copy.emailPlaceholder}
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    required
                  />
                  <input type="submit" value={copy.subscribe} />
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
      <div className="footer-bottom">
        <div className="container">
          <div className="row y-middle">
            <div className="col-lg-6 md-mb-10 text-lg-end text-center order-last">
              <ul className="copy-right-menu">
                {footerNav.map((item) => (
                  <li key={item.to}>
                    <Link to={item.to}>{item.label}</Link>
                  </li>
                ))}
                <li>
                  <a href="https://api.qtechcontracting.com/admin/login" target="_blank" rel="noopener noreferrer">
                    {locale === 'ar' ? 'لوحة التحكم' : 'Admin'}
                  </a>
                </li>
              </ul>
            </div>
            <div className="col-lg-6">
              <div className="copyright text-lg-start text-center">
                <p>
                  © {new Date().getFullYear()} {companyInfo.name}. Part of {companyInfo.parentGroup}.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
